import React, { useRef } from 'react';
import styled from 'styled-components';
import {
  AccordionDetails,
  AccordionSummary,
  AccordionProps,
} from '@mui/material';
import { ReactComponent as ExpandIcon } from '../../assets/arrow-down-expand.svg';
import { ReactComponent as VIcon } from '../../assets/v.svg';
import { Accordion } from '../Accordion';
import { Flex } from '../Flex';

const AccordionStyled = styled(Accordion)<{
  $variant: 'primary' | 'secondary';
}>`
  &.MuiAccordion-root {
    border: ${({ theme, $variant }) =>
      $variant === 'primary'
        ? `1px solid ${theme.palette.common.gray5}`
        : 'none'};
    border-radius: 8px;
  }
`;

const GreenVIcon = styled(VIcon)`
  path {
    stroke: ${({ theme }) => theme.palette.success.main};
  }
`;

const AccordionSummaryStyled = styled(AccordionSummary)<{
  $variant: 'primary' | 'secondary';
  $isExpanded?: boolean;
}>`
  background-color: ${({ theme, $variant }) =>
    $variant === 'primary'
      ? theme.palette.common.gray3
      : theme.palette.common.white};
  padding: ${({ $variant }) => ($variant === 'primary' ? '2px 24px' : '0')};
  width: ${({ $variant }) =>
    $variant === 'primary' ? undefined : 'fit-content'};
  gap: ${({ $variant }) => ($variant === 'primary' ? '24px' : '12px')};
  height: ${({ $variant }) => ($variant === 'primary' ? '44px' : undefined)}
  min-height: 0;
  flex-direction: row-reverse;
  border-radius: 8px;
  & .MuiAccordionSummary-content {
    margin: ${({ $variant }) => ($variant === 'primary' ? undefined : '0')}
    margin-left:  ${({ $variant }) => ($variant === 'primary' ? '24px' : '0')}
  }
  & .MuiAccordionSummary-expandIconWrapper {
    transform: rotate(-90deg);
  }
  & .MuiAccordionSummary-expandIconWrapper.Mui-expanded {
    transform: rotate(0deg);
  }      
  &.Mui-expanded {
    border-radius: 8px 8px 0 0;
  }
`;

const AccordionDetailsStyled = styled(AccordionDetails)<{
  $variant: 'primary' | 'secondary';
}>`
  border-top: ${({ $variant }) =>
    $variant === 'primary' ? '1px solid' : 'none'};
  border-color: ${({ theme }) => theme.palette.common.gray5};
  padding: 0;
`;

const ExpandIconStyled = styled(ExpandIcon)`
  width: 14px;
`;

type Props = Omit<AccordionProps, 'variant'> & {
  summary: React.ReactNode;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  checked?: boolean;
};

const TestAccordion = ({
  summary,
  children,
  variant = 'primary',
  checked,
  expanded,
  ...props
}: Props) => {
  const ref = useRef<any>();

  const handleChange = (e: any, val: boolean) => {
    if (val && (expanded === undefined || expanded)) {
      setTimeout(() => {
        ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }, 200);
    }
    props.onChange?.(e, val);
  };

  return (
    <AccordionStyled
      {...props}
      ref={ref}
      $variant={variant}
      expanded={expanded}
      onChange={handleChange}
    >
      <AccordionSummaryStyled
        $variant={variant}
        expandIcon={<ExpandIconStyled />}
      >
        <Flex sx={{ alignItems: 'center', gap: 3, width: '100%' }}>
          {summary}
          {checked && <GreenVIcon />}
        </Flex>
      </AccordionSummaryStyled>
      <AccordionDetailsStyled $variant={variant}>
        {children}
      </AccordionDetailsStyled>
    </AccordionStyled>
  );
};

export default TestAccordion;
